/**
 * AI Chat Page — streaming chat interface for the IT Maturity Assessment AI assistant.
 *
 * Built using existing shadcn primitives (Card, Button, ScrollArea).
 * Supports real-time SSE streaming with markdown rendering.
 */
import { useState, useRef, useEffect } from 'react'
import { Send, Trash2, Bot, User, Sparkles, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useAIChat, type ChatMessage } from '@/features/ai/hooks'
import { useAIStatus } from '@/features/ai/hooks'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { motion, AnimatePresence } from 'framer-motion'

function MessageBubble({ message }: { message: ChatMessage }) {
    const isUser = message.role === 'user'

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
        >
            {/* Avatar */}
            <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center
                ${isUser
                    ? 'bg-blue-500 text-white'
                    : 'bg-gradient-to-br from-violet-500 to-purple-600 text-white'
                }`}
            >
                {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
            </div>

            {/* Content */}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm
                ${isUser
                    ? 'bg-blue-500 text-white rounded-br-md'
                    : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                }`}
            >
                {isUser ? (
                    <p className="whitespace-pre-wrap">{message.content}</p>
                ) : (
                    <div className="prose prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-code:text-purple-600 prose-code:bg-purple-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.content || '●'}
                        </ReactMarkdown>
                    </div>
                )}
            </div>
        </motion.div>
    )
}

export function AIAssistantPage() {
    const { messages, isStreaming, sendMessage, clearMessages } = useAIChat()
    const { data: status } = useAIStatus()
    const [input, setInput] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLTextAreaElement>(null)

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    // Auto-resize textarea
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto'
            inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 200) + 'px'
        }
    }, [input])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        const trimmed = input.trim()
        if (!trimmed || isStreaming) return
        setInput('')
        sendMessage(trimmed)
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit(e)
        }
    }

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-semibold text-gray-900">AI Assistant</h1>
                        <p className="text-xs text-gray-500">
                            {status?.model ? `Model: ${status.model}` : 'IT Maturity Assessment AI'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {messages.length > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={clearMessages}
                            disabled={isStreaming}
                            className="text-gray-500"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Clear
                        </Button>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <Card className="flex-1 overflow-hidden border-gray-200">
                <CardContent className="h-full p-0">
                    <div className="h-full overflow-y-auto p-6 space-y-4">
                        {messages.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-center">
                                <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mb-4">
                                    <Bot className="h-8 w-8 text-purple-600" />
                                </div>
                                <h2 className="text-lg font-medium text-gray-900 mb-1">
                                    How can I help?
                                </h2>
                                <p className="text-sm text-gray-500 max-w-sm">
                                    Ask me about IT maturity concepts, assessment questions,
                                    scoring logic, or project initiatives.
                                </p>
                                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md">
                                    {[
                                        'Explain the maturity level scoring logic',
                                        'Suggest cybersecurity questions for Level 3',
                                        'What are best practices for IT governance?',
                                        'Help me plan a cloud migration initiative',
                                    ].map((suggestion) => (
                                        <button
                                            key={suggestion}
                                            onClick={() => sendMessage(suggestion)}
                                            className="text-left text-xs text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg px-3 py-2 border border-gray-200 transition-colors"
                                        >
                                            {suggestion}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <AnimatePresence>
                                {messages.map((msg) => (
                                    <MessageBubble key={msg.id} message={msg} />
                                ))}
                            </AnimatePresence>
                        )}

                        {isStreaming && (
                            <div className="flex items-center gap-2 text-xs text-gray-400 pl-11">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                AI is thinking...
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>
                </CardContent>
            </Card>

            {/* Input Area */}
            <form onSubmit={handleSubmit} className="mt-3">
                <div className="flex items-end gap-2 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent transition-shadow">
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Send a message..."
                        rows={1}
                        className="flex-1 resize-none border-0 bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none"
                        disabled={isStreaming}
                    />
                    <Button
                        type="submit"
                        size="icon"
                        disabled={!input.trim() || isStreaming}
                        className="h-8 w-8 rounded-lg bg-blue-500 hover:bg-blue-600 shrink-0"
                    >
                        <Send className="h-4 w-4" />
                    </Button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 text-center">
                    AI responses may be inaccurate. Verify important information.
                </p>
            </form>
        </div>
    )
}
